import React from 'react';
import { Star } from 'lucide-react';

// Function to generate a gradient between accent and navy with opacity variation
const generateGradient = () => {
  // Base colors
  const accent = '#49A0FF';
  const navy = '#002349';
  
  // Generate random opacity values between 0.7 and 1 for each color
  const opacity1 = 0.7 + Math.random() * 0.3;
  const opacity2 = 0.7 + Math.random() * 0.3;
  
  // Convert hex to rgba
  const getRGBA = (hex: string, opacity: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  return `linear-gradient(135deg, ${getRGBA(accent, opacity1)}, ${getRGBA(navy, opacity2)})`;
};

// Profile image component with brand colors
const ProfileImage = ({ name }: { name: string }) => {
  return (
    <div 
      className="w-12 h-12 rounded-full flex items-center justify-center relative overflow-hidden"
      style={{
        background: generateGradient(),
      }}
    >
      <span className="text-white font-bold text-lg relative z-10">{name[0].toUpperCase()}</span>
    </div>
  );
};

type Testimonial = {
  name: string;
  location: string;
  quote: string;
  rating: number;
};

const testimonials: Testimonial[] = [
  {
    name: "Lisa M.",
    location: "Kennewick, WA",
    quote: "I can't say enough about Monroe. He is professional, easy to get along with, funny, responsive to questions and concerns, reassuring, timely, experienced and straightforward. He goes above and beyond what one might expect of a real estate professional; he thoroughly researched a concern we had about the property's recent designation by FEMA to be in a floodplain. wholeheartedly and unreservedly recommend Monroe Davis as a superb real estate agent and am more than happy to be included on his list of references.",
    rating: 5
  },
  {
    name: "Sarah K.",
    location: "Richland, WA",
    quote: "I was considering selling my house a couple of months ago, so I reached out to Monroe with a list of questions. I told him there was no rush on getting back to me, as I was on the fence about selling. He called me back within an hour! That in itself is unheard of these days! Not only did he answer all of my questions, but he offered some great suggestions and recent stats on comparable home sales in my neighborhood. Monroe knew I wasn't sure yet about selling my house, and he still took the time to put together a list of homes to show me that fit my wish list AND my budget perfectly! Monroe was in my moment and made my needs his top priority, and he continues to do so! Thank you Monroe Davis!",
    rating: 5
  },
  {
    name: "Walter Fields",
    location: "Yakima, WA",
    quote: "Monroe Davis is an incredible realtor! He recently helped me sell my house in Richland. He is a fabulous asset to have on your side when going through the process of selling a home, and we will recommend him to everyone we know whether buying or selling. He is extremely knowledgeable about the process from preparing the house, listing the house, and right up to the signing of the final paperwork. This is my first house, and I had so many questions, all of which he answered with patience and knowledge.",
    rating: 5
  },
  {
    name: "LB Rock III MD",
    location: "Issaquah, WA",
    quote: '"York"—as I affectionately and somewhat teasingly call him—and I have known each other since 2005 when he was working as a tile setter and very beautifully and flawlessly set the kitchen and bathroom countertops, master bath shower walls, and floors in both bathrooms of the house I was then building and which we just sold…17 years later. His attention to detail then, as a tile setter, carried over later when he acted as my real estate agent.',
    rating: 5
  }
];

const renderStars = (rating: number) => {
  return Array.from({ length: 5 }).map((_, index) => (
    <Star
      key={index}
      className={`w-4 h-4 ${
        index < rating ? 'text-[#49A0FF] fill-[#49A0FF]' : 'text-gray-300'
      }`}
    />
  ));
};

export default function Testimonials() {
  return (
    <section className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-navy-900">My Clients</h2>
          <p className="mt-4 text-lg text-gray-600">See what others have to say</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300 flex flex-col"
            >
              <div className="flex items-center mb-4">
                <ProfileImage name={testimonial.name} />
                <div className="ml-3">
                  <div className="font-semibold text-navy-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.location}</div>
                </div>
              </div>
              <div className="flex mb-4">{renderStars(testimonial.rating)}</div>
              <blockquote className="text-gray-700 italic flex-grow">
                "{testimonial.quote}"
              </blockquote>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}